import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FileSignature, KeyRound, Search, Plus, Building2, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
  useOrgPermits,
  useJurisdictions,
  useSavePortalCredentials,
  useSyncSdlPermits,
  permitStatusLabel,
} from "@/hooks/usePermits";

const STATUS_TONE: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  submitted: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-400",
  under_review: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-400",
  issued: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  final: "bg-emerald-600/15 text-emerald-700 dark:text-emerald-400",
  corrections_required: "bg-red-500/15 text-red-700 dark:text-red-400",
};

export default function PermitsHub() {
  const { orgId } = useAuth();
  const navigate = useNavigate();
  const { data: permits, isLoading } = useOrgPermits(orgId);
  const sync = useSyncSdlPermits();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filtered = (permits ?? []).filter((p: { status: string; permit_number?: string | null; job?: { customer_name?: string; address?: string } | null; jurisdiction?: { name?: string } | null; property_address?: string | null }) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (!search) return true;
    const hay = `${p.permit_number ?? ""} ${p.job?.customer_name ?? ""} ${p.job?.address ?? p.property_address ?? ""} ${p.jurisdiction?.name ?? ""}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  const groups = {
    open: filtered.filter((p: { status: string }) =>
      ["draft","prepping_docs","ready_to_submit","submitted","under_review","corrections_required"].includes(p.status)),
    active: filtered.filter((p: { status: string }) =>
      ["approved","issued","inspections"].includes(p.status)),
    closed: filtered.filter((p: { status: string }) =>
      ["final","closed","cancelled","rejected"].includes(p.status)),
  };

  return (
    <AppLayout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileSignature className="h-6 w-6 text-primary" /> Permits
            </h1>
            <p className="text-sm text-muted-foreground">
              Pull, pay, and track permits across every jurisdiction. Powered by Bridgette + SDL automation.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              onClick={() => orgId && sync.mutate({ org_id: orgId })}
              disabled={!orgId || sync.isPending}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${sync.isPending ? "animate-spin" : ""}`} />
              {sync.isPending ? "Syncing SDL…" : "Sync from SDL"}
            </Button>
            <PortalCredentialsDialog />
          </div>
        </div>

        <Card>
          <CardContent className="p-3 flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                placeholder="Search permit #, customer, address, city…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries({
                  draft: "Draft",
                  submitted: "Submitted",
                  under_review: "Under review",
                  corrections_required: "Corrections",
                  issued: "Issued",
                  inspections: "Inspections",
                  final: "Finaled",
                }).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading permits…</div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground space-y-2">
              <p>No permits yet.</p>
              <p>Hit <strong>Sync from SDL</strong> to pull every permit from your connected SDL account, or file a new one from a job.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <PermitGroup title="Open / In review" items={groups.open} onOpen={(jobId) => jobId && navigate(`/jobs/${jobId}`)} />
            <PermitGroup title="Issued / In inspection" items={groups.active} onOpen={(jobId) => jobId && navigate(`/jobs/${jobId}`)} />
            <PermitGroup title="Closed" items={groups.closed} onOpen={(jobId) => jobId && navigate(`/jobs/${jobId}`)} />
          </>
        )}
      </div>
    </AppLayout>
  );
}

function PermitGroup({
  title,
  items,
  onOpen,
}: {
  title: string;
  // deno-lint-ignore no-explicit-any
  items: any[];
  onOpen: (jobId: string) => void;
}) {
  if (!items.length) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title} <span className="text-muted-foreground text-xs">({items.length})</span></CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        {items.map((p) => (
          <button
            key={p.id}
            onClick={() => onOpen(p.job_id)}
            className="flex items-center gap-3 text-left border rounded-md p-3 hover:border-primary/40 transition"
          >
            <Badge variant="outline" className={STATUS_TONE[p.status] ?? ""}>
              {permitStatusLabel(p.status)}
            </Badge>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {p.job?.customer_name ?? p.property_address ?? "From SDL"} · {p.permit_type}
                {p.permit_number ? ` · #${p.permit_number}` : ""}
              </div>
              <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                {p.jurisdiction?.name ?? "Jurisdiction TBD"} · {p.job?.address ?? p.property_address ?? ""}
                {p.source === "sdl_sync" ? " · synced from SDL" : ""}
              </div>
            </div>
            <div className="text-xs text-right">
              <div>${(p.total_fees ?? 0).toFixed(2)}</div>
              <div className="text-muted-foreground">
                {p.applied_at ? new Date(p.applied_at).toLocaleDateString() : "Not submitted"}
              </div>
            </div>
          </button>
        ))}
      </CardContent>
    </Card>
  );
}

function PortalCredentialsDialog() {
  const { user, orgId } = useAuth();
  const { data: jurisdictions } = useJurisdictions();
  const save = useSavePortalCredentials();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    jurisdiction_id: "",
    label: "SDL — primary",
    username: "",
    password: "",
    consent: false,
  });

  const submit = async () => {
    if (!user || !orgId) return;
    if (!form.username || !form.password) return;
    await save.mutateAsync({
      org_id: orgId,
      jurisdiction_id: form.jurisdiction_id || null,
      label: form.label,
      username: form.username,
      password: form.password,
      consent_to_automate: form.consent,
    });
    setOpen(false);
    setForm({ jurisdiction_id: "", label: "SDL — primary", username: "", password: "", consent: false });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <KeyRound className="h-4 w-4 mr-1" /> SDL credentials
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save SDL portal credentials</DialogTitle>
          <DialogDescription>
            Used by the headless filer to submit permits on your behalf. Password is encrypted at rest.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Jurisdiction (optional — leave blank for any SDL city)</Label>
            <Select
              value={form.jurisdiction_id}
              onValueChange={(v) => setForm((f) => ({ ...f, jurisdiction_id: v }))}
            >
              <SelectTrigger><SelectValue placeholder="Any SDL jurisdiction" /></SelectTrigger>
              <SelectContent>
                {(jurisdictions ?? []).filter((j) => j.portal_type === "sdl").map((j) => (
                  <SelectItem key={j.id} value={j.id}>{j.name} · {j.state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Label</Label>
            <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
          </div>
          <div>
            <Label>Username / email</Label>
            <Input
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              autoComplete="off"
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              autoComplete="new-password"
            />
          </div>
          <div className="flex items-start gap-2 rounded-md border p-3 bg-muted/30">
            <Switch
              checked={form.consent}
              onCheckedChange={(v) => setForm((f) => ({ ...f, consent: v }))}
            />
            <div className="text-xs text-muted-foreground">
              I authorize this app to log in to the SDL portal on my behalf and submit permit
              applications + pay fees with the saved card on file. Every action is screenshot-logged.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={save.isPending || !form.username || !form.password}>
            <Plus className="h-4 w-4 mr-1" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
